import * as React from 'react';
import styled, { css } from 'styled-components';

import { getMessagesList } from '../../api/messages';
import RichMessageText from '../../molecules/RichMessageText';
import Loader from '../../atoms/Loader';
import { formatApiDateTime } from '../../utils/parseApiDateTime';

/**
 * Branch Admin dashboard block: today's messages, body only.
 *
 * Deliberately not a Table. Addresses / date columns squeezed the message into
 * a narrow strip, which is the one thing the reader is here for.
 *
 * `?active=true` rather than `?date=<today>`: the latter asks when a message
 * was *published*, so a holiday notice written weeks before its window opens
 * would never appear on the day it is actually showing.
 *
 * `?received=true` because a Branch Admin's list rule returns every message in
 * their branches - right for Settings > Messages, wrong for a block that reads
 * as an inbox. It showed messages sent to clients. A branch-wide employee
 * broadcast does reach a manager; anything aimed at clients or inspectors, or
 * addressed to named people, does not.
 */
const FADailyMessage = () => {
  const [items, setItems] = React.useState<any[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);

  React.useEffect(() => {
    let cancelled = false;

    getMessagesList({
      active: true,
      received: true,
      limit: 25,
      offset: 0,
    })
      .then(response => {
        if (cancelled) return;
        setItems(
          (response.data?.results || []).filter(
            row => row && row.text,
          ),
        );
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Wrapper>
      <Header>Messages of the Day</Header>
      <Content>
        {isLoading && (
          <Centered>
            <Loader />
          </Centered>
        )}

        {!isLoading && !items.length && (
          <Empty>No messages today.</Empty>
        )}

        {!isLoading &&
          items.map(item => (
            <Item key={item.id}>
              <Meta>
                {/* `sender` is a SQL Concat of first+last name, so it can come
                    back as a lone space on a user with neither. */}
                {(item.sender || '').trim() && (
                  <Sender>{item.sender.trim()}</Sender>
                )}
                {formatApiDateTime(item.publicationDate) && (
                  <Sent>
                    {formatApiDateTime(item.publicationDate)}
                  </Sent>
                )}
              </Meta>
              <RichMessageText html={item.text} />
            </Item>
          ))}
      </Content>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  background: var(--neutral-0);
  border: 1px solid var(--border);
  border-radius: 10px;
  box-shadow: var(--shadow-xs);
  padding: 24px;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
`;

const Header = styled.div`
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--mainText);
  margin-bottom: 20px;
`;

const Content = styled.div`
  flex: 1;
  min-height: 240px;
  max-height: 320px;
  overflow-y: auto;
  overflow-x: hidden;
`;

const Item = styled.div`
  font-size: 0.9375rem;
  line-height: 1.55;
  color: var(--mainText);

  & + & {
    margin-top: 14px;
    padding-top: 14px;
    border-top: 1px solid var(--border);
  }
`;

const Meta = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 4px 8px;
  margin-bottom: 4px;
`;

/* One type treatment for the whole byline - the name and the timestamp read
   as a single line, so they share size, weight and colour. Change it here and
   both follow. */
const metaText = css`
  font-size: 0.75rem;
  font-weight: 400;
  color: var(--neutral-500);
`;

const Sender = styled.span`
  ${metaText}
`;

const Sent = styled.span`
  ${metaText}
`;

const Centered = styled.div`
  display: flex;
  justify-content: center;
  padding: 32px 0;
`;

const Empty = styled.div`
  text-align: center;
  padding: 32px 0;
  font-size: 0.875rem;
  color: var(--neutral-500);
`;

export default FADailyMessage;
